import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { ToastService } from './toast.service';

interface BackendErrorShape {
  isSuccess?: boolean;
  message?: string;
  error?: {
    code?: string;
    message?: string;
  };
  errors?: Record<string, unknown>;
}

function isBackendErrorShape(payload: unknown): payload is BackendErrorShape {
  return !!payload && typeof payload === 'object';
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);

  if (req.url.includes('/api/v1/auth/refresh-token')) {
    return next(req);
  }

  return next(req).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) {
        return;
      }

      const message = extractBusinessErrorMessage(event.body);
      if (message && /the value ['"]null['"] is not valid\.?/i.test(message)) {
        toastService.error('Please check your details and try again.');
      } else if (message) {
        toastService.errorMessage(message, 5000);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      const message = extractErrorMessage(error);
      if (message && /the value ['"]null['"] is not valid\.?/i.test(message)) {
        toastService.error('Please check your details and try again.');
      } else if (message) {
        toastService.errorMessage(message, 5000);
      }

      return throwError(() => error);
    }),
  );
};

function extractBusinessErrorMessage(payload: unknown): string | null {
  if (!isBackendErrorShape(payload) || payload.isSuccess !== false) {
    return null;
  }

  if (payload.error?.message) {
    return payload.error.message;
  }

  if (payload.message) {
    return payload.message;
  }

  return null;
}

function extractErrorMessage(error: HttpErrorResponse): string | null {
  const payload = error.error as unknown;

  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }

  if (isBackendErrorShape(payload) && payload.error?.message) {
    return payload.error.message;
  }

  if (isBackendErrorShape(payload) && payload.message) {
    return payload.message;
  }

  if (isBackendErrorShape(payload) && payload.errors) {
    const firstError = Object.values(payload.errors)[0];

    if (Array.isArray(firstError) && firstError[0]) {
      return String(firstError[0]);
    }

    if (typeof firstError === 'string') {
      return firstError;
    }
  }

  if (error.status === 0) {
    return 'Unable to connect to the server. Please try again.';
  }

  return null;
}
